import React, { PropTypes as T } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, formValueSelector } from 'redux-form/immutable';
import { SingleDatePicker } from 'react-dates';

import { loadingCampaign } from '../../containers/campaign/actions';
import { setCampaignPermission } from '../../containers/influencers-campaign-list/actions';
import TextInput from '../../components/form-input-text';
import CurrencyInput from '../../components/form-input-currency';
import { setStatusBar } from '../../containers/status-bar/actions';
import { sendBirdService } from '../../utils/sendbird-service';
import { getOrganisationByName } from '../../utils/jsonapi-functions/organisations';
import { createCampaign, addChatDiscussionToCamapign, addCampaignPermission } from '../../utils/jsonapi-functions/campaigns';
import { createCampaignPermission } from '../../utils/jsonapi-functions/campaign-permissions';
import { createDiscussion } from '../../utils/jsonapi-functions/discussions';
import { CAMPAIGN_OWNER } from '../../utils/static-configuration';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';

class CampaignCreate extends React.Component {
  static contextTypes = {
    router: T.object,
  };

  constructor(props, context) {
    super(props, context);

    this.state = {
      createCampaignLoading: false,
      liveDateFocused: false,
    };
  }

  componentWillUnmount() {
    this.props.reset(); // Reset form values
  }

  async createCampaign(formData) {
    this.setState({ createCampaignLoading: true });

    const campaignName = formData.get('campaignName');
    const campaignBudget = formData.get('campaignBudget') || 0;
    const liveDate = formData.get('liveDate') ? formData.get('liveDate').toISOString() : null;
    const userId = this.props.userData.get('id');
    const organisationId = this.props.userData.get('organisationId');
    const email = this.props.userData.get('email');


    try {
      const campaignData = {
        name: campaignName,
        budget: campaignBudget,
        campaignType: 'WORKFLOW',
        liveDate,
      };

      const campaign = await createCampaign(campaignData, 'no');
      const campaignJSON = campaign.toJSONTree();

      const campaignPermissionData = {
        campaignId: campaignJSON.id,
        userId,
        email,
        role: CAMPAIGN_OWNER,
        organisationId,
      };
      const campaignPermission = await createCampaignPermission(campaignPermissionData);
      const campaignPermissionJSON = campaignPermission.toJSONTree();
      console.log(campaignPermissionJSON);
      // Login user
      await sendBirdService.connectSendbirdUser(userId);
      console.log('sendbird user connected');

      // Make both client and team member discussion and add them as relationships
      const ownerPermissionId = campaignPermission.toJSON().id;


      const createTeamDiscussion = false; // we have switched off team discussion at the minute.

      if (createTeamDiscussion) {
        console.log('creating team discussion');
        const teamDiscussion = await this.createDiscussion('Team', campaignJSON.id, userId, ownerPermissionId, [campaignJSON.owner.id]);
        await addChatDiscussionToCamapign(campaign, 'teamDiscussion', teamDiscussion);
      } else {
        console.log('NOT creating team discussion!');
      }

      await addCampaignPermission(campaign, campaignPermission);

      const campaignCreateData = {
        campaignId: campaignJSON.id,
        campaignName,
        campaignBudget,
        includesClient: false,
      };

      trackEvent('Create Campaign', campaignCreateData);
      console.log('about to set props');

      this.props.setCampaignData(campaignJSON.id, campaignName);
      this.props.setCampaignCreated(true);
      this.props.onSetCampaignPermission(campaignPermissionJSON);
      this.props.onLoadingCampaign(campaignJSON.id);
      console.log('finished');
    } catch (error) {
      reportError('CREATE_CAMPAIGN_ERROR', error.message);
      this.props.onSetStatusBar(error.message, 'red');
    }
  }

  async createDiscussion(discussionName, campaignId, ownerId, ownerPermissionId, organisationIds) {
    const sendbirdMembers = [ownerId];

    let channelURL;
    try {
      channelURL = await sendBirdService.createSendbirdChannel(sendbirdMembers, discussionName);
    } catch (error) {
      reportError('CREATE_SENDBIRD_CHANNEL_ERROR', error.message);
      this.props.onSetStatusBar(`Error setting up collaborations: ${error.message}`, 'red');
    }

    const discussionMembers = [
      { type: 'campaignPermission', id: ownerPermissionId },
    ];

    const organisationsList = [];
    organisationIds.forEach(organisation => organisationsList.push({ type: 'organisations', id: organisation }));

    const discussionData = {
      name: discussionName,
      members: discussionMembers,
      channelURL,
      campaignId,
      organisationsList,
    };

    try {
      const discussion = await createDiscussion(discussionData);
      return discussion;
    } catch (error) {
      reportError('CREATE_DISCUSSION_ERROR', error.message);
      this.props.onSetStatusBar(error.message, 'red');
    }
    return null;
  }

  render() {
    const { handleSubmit } = this.props;
    const { createCampaignLoading } = this.state;

    return (

      <div>
        <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
          <div className="hero">
            <h1>Create a campaign</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit(this.createCampaign.bind(this))}>
          <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
            <h2>Name your campaign</h2>
            <div className="spacer" />
            <p>Make sure it’s nice and descriptive. This is what people will see when you add them to the campaign.</p>
            <div className="spacer" />
            <Field name="campaignName" type="text" label="" component={TextInput} placeholder="Silver Spoon Company Q3 Campaign" />
          </div>
          <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-4-6 grid-xl-4-6">
            <h2>Influencer budget <span className="light">(optional)</span></h2>
            <div className="spacer" />
            <p>The total campaign budget will be visible to your team members only. You can use this to decide how much money to budget for each influencer and to make secure payments to them.</p>
            <Field name="campaignBudget" component={CurrencyInput} placeholder={0} />
          </div>
          <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-4-6 grid-xl-4-6">
            <h2>Live date</h2>
            <div className="spacer small" />
            <p>This will be the default live date for deliverables created on this campaign.</p>
            <div className="spacer small" />
            <Field
              name="liveDate"
              component={props => {
                const { input: { value, onChange } } = props;
                return (
                  <div>
                    <SingleDatePicker
                      id="liveDate"
                      {...props}
                      date={value || null}
                      focused={this.state.liveDateFocused}
                      numberOfMonths={1}
                      displayFormat={'DD/MM/YYYY'}
                      placeholder="Select a date"
                      onDateChange={date => onChange(date)}
                      onFocusChange={({ focused }) => this.setState({ liveDateFocused: focused })}
                    />
                    { props.meta.touched && props.meta.error &&
                      <div>
                        <div className="spacer small" />
                        <p className="small error">{ props.meta.error }</p>
                      </div>
                    }
                  </div>
                );
              }}
            />
          </div>

          <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-2-6 grid-xl-2-6" />
          <div className="grid-6-6 grid-sm-6-6 grid-md-6-6 grid-lg-6-6 grid-xl-6-6">
            <div className="hero">
              <button className="large rose wide" type="submit">
                { createCampaignLoading
                  ? <ul className="dots">
                    <li id="dot1" />
                    <li />
                    <li id="dot2" />
                    <li />
                    <li id="dot3" />
                  </ul>
                  : <p>create campaign</p>
                }
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

CampaignCreate.propTypes = {
  routeParams: React.PropTypes.object,
};

function validate(values) {
  const errors = {};

  const campaignName = values.get('campaignName');
  const liveDate = values.get('liveDate');

  if (!campaignName) errors.campaignName = 'Enter a campaign name.';
  if (!liveDate) errors.liveDate = 'Enter the date the campaign goes live.';

  return errors;
}

const selector = formValueSelector('NewCampaignForm');

const mapStateToProps = state => ({
  currentUser: state.userData.get('currentUser'),
  userData: state.userData.get('userData'),
  hasClient: 'no',
  campaignBrief: selector(state, 'campaignBrief'),
  initialValues: {
    hasClient: 'no',
  },
});

export function mapDispatchToProps(dispatch) {
  return {
    onSetStatusBar: (message, colour) => dispatch(setStatusBar(message, colour)),
    onLoadingCampaign: campaignId => dispatch(loadingCampaign(campaignId)),
    onSetCampaignPermission: campaignPermission => dispatch(setCampaignPermission(campaignPermission)),
    dispatch,
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'NewCampaignForm',
    validate,
  })(CampaignCreate),
);



/** WEBPACK FOOTER **
 ** ./src/containers/campaign-new/campaign-create.js
 **/